<template>
  <div class="admin-list">
    <!-- 页面头部 -->
    <div class="page-header">
      <div class="header-content">
        <div class="header-left">
          <div class="header-info">
            <h1 class="page-title">
              <i class="el-icon-user"></i>
              管理员管理
            </h1>
            <p class="page-subtitle">管理系统管理员账户和权限</p>
          </div>
        </div>
        <div class="header-actions">
          <el-button size="medium" @click="refreshData">
            <i class="el-icon-refresh"></i> 刷新数据
          </el-button>
          <el-button type="primary" size="medium" @click="handleCreate">
            <i class="el-icon-plus"></i> 新增管理员
          </el-button>
        </div>
      </div>
    </div>

    <!-- 主要内容区域 -->
    <div class="main-content">
      <!-- 统计卡片 -->
      <div class="stats-section">
        <div class="stat-card">
          <div class="stat-icon super">
            <i class="el-icon-s-custom"></i>
          </div>
          <div class="stat-info">
            <div class="stat-value">{{ statistics.superAdmin || 0 }}</div>
            <div class="stat-label">超级管理员</div>
          </div>
        </div>

        <div class="stat-card">
          <div class="stat-icon system">
            <i class="el-icon-setting"></i>
          </div>
          <div class="stat-info">
            <div class="stat-value">{{ statistics.systemAdmin || 0 }}</div>
            <div class="stat-label">系统管理员</div>
          </div>
        </div>

        <div class="stat-card">
          <div class="stat-icon store">
            <i class="el-icon-office-building"></i>
          </div>
          <div class="stat-info">
            <div class="stat-value">{{ statistics.storeAdmin || 0 }}</div>
            <div class="stat-label">门店管理员</div>
          </div>
        </div>

        <div class="stat-card">
          <div class="stat-icon total">
            <i class="el-icon-user-solid"></i>
          </div>
          <div class="stat-info">
            <div class="stat-value">{{ statistics.total || 0 }}</div>
            <div class="stat-label">总管理员数</div>
          </div>
        </div>
      </div>

      <!-- 筛选区域 -->
      <div class="filter-section">
        <div class="filter-card">
          <el-form :model="searchForm" :inline="true" class="filter-form">
            <div class="form-row">
              <div class="form-item">
                <label class="form-label">搜索关键词</label>
                <el-input
                  v-model="searchForm.keyword"
                  placeholder="请输入用户名或姓名"
                  clearable
                  size="large"
                  prefix-icon="el-icon-search"
                />
              </div>
              
              <div class="form-item">
                <label class="form-label">管理员角色</label>
                <el-select 
                  v-model="searchForm.roleType" 
                  placeholder="全部角色" 
                  clearable
                  size="large"
                >
                  <el-option label="超级管理员" :value="1" />
                  <el-option label="系统管理员" :value="2" />
                  <el-option label="门店管理员" :value="3" />
                </el-select>
              </div>
              
              <div class="form-item">
                <label class="form-label">账户状态</label>
                <el-select 
                  v-model="searchForm.isActive" 
                  placeholder="全部状态" 
                  clearable
                  size="large"
                >
                  <el-option label="启用" :value="true" />
                  <el-option label="禁用" :value="false" />
                </el-select>
              </div>
            </div>
            
            <div class="form-actions">
              <el-button type="primary" @click="handleSearch" size="large">
                <i class="el-icon-search"></i> 搜索
              </el-button>
              <el-button @click="resetSearch" size="large">
                <i class="el-icon-refresh-left"></i> 重置
              </el-button>
            </div>
          </el-form>
        </div>
      </div>

      <!-- 表格区域 -->
      <div class="table-section">
        <div class="table-card">
          <div class="table-header">
            <h3>
              <i class="el-icon-menu"></i>
              管理员列表
            </h3>
            <p>共 {{ pagination.total }} 条记录</p>
          </div>

          <el-table
            v-loading="loading"
            :data="adminList"
            style="width: 100%"
            class="admin-table"
          >
            <el-table-column prop="id" label="ID" width="80" align="center">
              <template slot-scope="scope">
                <span class="admin-id">#{{ scope.row.id }}</span>
              </template>
            </el-table-column>

            <el-table-column label="用户信息" min-width="200">
              <template slot-scope="scope">
                <div class="user-info">
                  <div class="user-avatar">
                    <i class="el-icon-user"></i>
                  </div>
                  <div class="user-details">
                    <div class="username">{{ scope.row.username }}</div>
                    <div class="real-name">{{ scope.row.realName || '-' }}</div>
                  </div>
                </div>
              </template>
            </el-table-column>

            <el-table-column label="联系方式" min-width="180">
              <template slot-scope="scope">
                <div class="contact-info">
                  <div class="contact-item" v-if="scope.row.phone">
                    <i class="el-icon-phone"></i>
                    <span>{{ scope.row.phone }}</span>
                  </div>
                  <div class="contact-item" v-if="scope.row.email">
                    <i class="el-icon-message"></i>
                    <span>{{ scope.row.email }}</span>
                  </div>
                </div>
              </template>
            </el-table-column>

            <el-table-column label="角色" width="120" align="center">
              <template slot-scope="scope">
                <el-tag 
                  :class="getRoleClass(scope.row.roleType)"
                  class="role-tag"
                >
                  <i :class="getRoleIcon(scope.row.roleType)"></i>
                  {{ scope.row.roleText }}
                </el-tag>
              </template>
            </el-table-column>

            <el-table-column label="门店信息" width="150">
              <template slot-scope="scope">
                <div class="store-info" v-if="scope.row.store_info">
                  <i class="el-icon-office-building"></i>
                  <span>{{ scope.row.store_info.store_name }}</span>
                </div>
                <span v-else class="no-store">-</span>
              </template>
            </el-table-column>

            <el-table-column label="状态" width="100" align="center">
              <template slot-scope="scope">
                <div class="status-wrapper">
                  <el-tag 
                    :class="scope.row.isActive ? 'status-active' : 'status-inactive'"
                    class="status-tag"
                  >
                    <i :class="scope.row.isActive ? 'el-icon-success' : 'el-icon-error'"></i>
                    {{ scope.row.isActive ? '启用' : '禁用' }}
                  </el-tag>
                </div>
              </template>
            </el-table-column>

            <el-table-column label="登录信息" min-width="160">
              <template slot-scope="scope">
                <div class="login-info">
                  <div class="login-item">
                    <i class="el-icon-time"></i>
                    最后登录
                  </div>
                  <div class="login-value">
                    {{ scope.row.lastLoginTime ? formatDate(scope.row.lastLoginTime) : '从未登录' }}
                  </div>
                  <div class="login-item">
                    <i class="el-icon-plus"></i>
                    创建时间
                  </div>
                  <div class="login-value">{{ formatDate(scope.row.createdAt) }}</div>
                </div>
              </template>
            </el-table-column>

            <el-table-column label="操作" width="200" fixed="right" align="center">
              <template slot-scope="scope">
                <div class="action-buttons">
                  <el-button 
                    type="primary" 
                    icon="el-icon-edit" 
                    size="mini" 
                    @click="handleEdit(scope.row)"
                    circle
                    class="action-btn edit-btn"
                    title="编辑"
                  />
                  <el-button 
                    type="warning" 
                    icon="el-icon-key" 
                    size="mini" 
                    @click="handleResetPassword(scope.row)"
                    circle
                    class="action-btn reset-btn"
                    title="重置密码"
                  />
                  <el-button
                    :type="scope.row.isActive ? 'info' : 'success'"
                    :icon="scope.row.isActive ? 'el-icon-video-pause' : 'el-icon-video-play'"
                    size="mini"
                    @click="handleToggleStatus(scope.row)"
                    circle
                    class="action-btn toggle-btn"
                    :title="scope.row.isActive ? '禁用' : '启用'"
                  />
                  <el-button
                    type="danger"
                    icon="el-icon-delete"
                    size="mini"
                    @click="handleDelete(scope.row)"
                    circle
                    class="action-btn delete-btn"
                    title="删除"
                  />
                </div>
              </template>
            </el-table-column>
          </el-table>
          
          <!-- 分页 -->
          <div class="pagination-wrapper">
            <el-pagination
              @size-change="handleSizeChange"
              @current-change="handleCurrentChange"
              :current-page="pagination.currentPage"
              :page-sizes="[10, 20, 50, 100]"
              :page-size="pagination.pageSize"
              layout="total, sizes, prev, pager, next, jumper"
              :total="pagination.total"
            />
          </div>
        </div>
      </div>
    </div>
    
    <!-- 新增/编辑对话框 -->
    <el-dialog
      :title="dialogTitle"
      :visible.sync="dialogVisible"
      width="600px"
      class="admin-dialog"
      @close="resetForm"
    >
      <div class="dialog-content">
        <el-form
          ref="adminForm"
          :model="adminForm"
          :rules="adminRules"
          label-width="120px"
          class="admin-form"
        >
          <div class="form-section">
            <h4><i class="el-icon-user"></i> 基本信息</h4>
            <div class="form-grid">
              <div class="form-item">
                <el-form-item label="用户名" prop="username">
                  <el-input
                    v-model="adminForm.username"
                    placeholder="请输入用户名"
                    :disabled="isEdit"
                    size="large"
                    prefix-icon="el-icon-user"
                  />
                </el-form-item>
              </div>
              
              <div class="form-item">
                <el-form-item label="真实姓名" prop="real_name">
                  <el-input 
                    v-model="adminForm.real_name" 
                    placeholder="请输入真实姓名" 
                    size="large"
                    prefix-icon="el-icon-s-custom"
                  />
                </el-form-item>
              </div>
            </div>

            <div class="form-grid">
              <div class="form-item">
                <el-form-item label="手机号" prop="phone">
                  <el-input 
                    v-model="adminForm.phone" 
                    placeholder="请输入手机号" 
                    size="large"
                    prefix-icon="el-icon-phone"
                  />
                </el-form-item>
              </div>
              
              <div class="form-item">
                <el-form-item label="邮箱" prop="email">
                  <el-input 
                    v-model="adminForm.email" 
                    placeholder="请输入邮箱" 
                    size="large"
                    prefix-icon="el-icon-message"
                  />
                </el-form-item>
              </div>
            </div>

            <div class="form-item full-width" v-if="!isEdit">
              <el-form-item label="初始密码" prop="password">
                <el-input
                  v-model="adminForm.password"
                  type="password"
                  placeholder="请输入初始密码（不少于6位）"
                  size="large"
                  prefix-icon="el-icon-lock"
                  show-password
                />
              </el-form-item>
            </div>
          </div>

          <div class="form-section">
            <h4><i class="el-icon-s-tools"></i> 权限配置</h4>
            <div class="form-grid">
              <div class="form-item">
                <el-form-item label="管理员角色" prop="role_type">
                  <el-select 
                    v-model="adminForm.role_type" 
                    placeholder="请选择角色" 
                    size="large"
                    style="width: 100%"
                  >
                    <el-option 
                      label="超级管理员" 
                      :value="1"
                    >
                      <span style="float: left">超级管理员</span>
                      <span style="float: right; color: #f56c6c; font-size: 13px">最高权限</span>
                    </el-option>
                    <el-option 
                      label="系统管理员" 
                      :value="2"
                    >
                      <span style="float: left">系统管理员</span>
                      <span style="float: right; color: #e6a23c; font-size: 13px">系统权限</span>
                    </el-option>
                    <el-option 
                      label="门店管理员" 
                      :value="3"
                    >
                      <span style="float: left">门店管理员</span>
                      <span style="float: right; color: #909399; font-size: 13px">门店权限</span>
                    </el-option>
                  </el-select>
                </el-form-item>
              </div>
              
              <div class="form-item" v-if="adminForm.role_type === 3">
                <el-form-item label="关联门店" prop="store_id">
                  <el-input 
                    v-model="adminForm.store_id" 
                    placeholder="请输入门店ID" 
                    size="large"
                    prefix-icon="el-icon-office-building"
                  />
                </el-form-item>
              </div>
            </div>

            <div class="role-description" v-if="adminForm.role_type">
              <div class="desc-item" :class="getRoleDescClass(adminForm.role_type)">
                <i :class="getRoleIcon(adminForm.role_type)"></i>
                <div class="desc-content">
                  <h5>{{ getRoleText(adminForm.role_type) }}</h5>
                  <p>{{ getRoleDescription(adminForm.role_type) }}</p>
                </div>
              </div>
            </div>
          </div>
        </el-form>
      </div>
      
      <div slot="footer" class="dialog-footer">
        <el-button @click="dialogVisible = false" size="large">取消</el-button>
        <el-button type="primary" @click="handleSubmit" size="large">
          <i class="el-icon-check"></i>
          {{ isEdit ? '保存修改' : '创建管理员' }}
        </el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import {
  getAdminList,
  createAdmin,
  updateAdmin,
  updateAdminStatus,
  resetAdminPassword,
  deleteAdmin
} from '@/api/admin'

export default {
  name: 'AdminList',
  data() {
    return {
      loading: false,
      adminList: [],
      statistics: {
        superAdmin: 0,
        systemAdmin: 0,
        storeAdmin: 0,
        total: 0
      },
      pagination: {
        currentPage: 1,
        pageSize: 20,
        total: 0,
        totalPages: 0
      },
      searchForm: {
        keyword: '',
        roleType: null,
        isActive: null
      },
      dialogVisible: false,
      isEdit: false,
      adminForm: {
        username: '',
        password: '',
        real_name: '',
        phone: '',
        email: '',
        role_type: null,
        store_id: null
      },
      adminRules: {
        username: [
          { required: true, message: '请输入用户名', trigger: 'blur' },
          { min: 3, max: 20, message: '用户名长度在 3 到 20 个字符', trigger: 'blur' }
        ],
        password: [
          { required: true, message: '请输入密码', trigger: 'blur' },
          { min: 6, message: '密码长度不能少于6位', trigger: 'blur' }
        ],
        real_name: [
          { required: true, message: '请输入真实姓名', trigger: 'blur' }
        ],
        phone: [
          { pattern: /^1[3-9]\d{9}$/, message: '请输入正确的手机号', trigger: 'blur' }
        ],
        email: [
          { type: 'email', message: '请输入正确的邮箱格式', trigger: 'blur' }
        ],
        role_type: [
          { required: true, message: '请选择角色', trigger: 'change' }
        ],
        store_id: [
          { required: true, message: '请输入门店ID', trigger: 'blur' }
        ]
      }
    }
  },
  computed: {
    dialogTitle() {
      return this.isEdit ? '编辑管理员' : '新增管理员'
    }
  },
  mounted() {
    this.loadAdminList()
  },
  methods: {
    async loadAdminList() {
      try {
        this.loading = true
        const params = {
          page: this.pagination.currentPage,
          pageSize: this.pagination.pageSize,
          ...this.searchForm
        }
        
        // 过滤空值
        Object.keys(params).forEach(key => {
          if (params[key] === null || params[key] === '') {
            delete params[key]
          }
        })
        
        const res = await getAdminList(params)
        this.adminList = res.data.list || []
        this.pagination = res.data.pagination || {
          currentPage: 1,
          pageSize: 20,
          total: 0,
          totalPages: 0
        }
        
        // 从响应中获取统计数据
        if (res.data.statistics) {
          this.statistics = {
            superAdmin: res.data.statistics.super_admins || 0,
            systemAdmin: res.data.statistics.system_admins || 0,
            storeAdmin: res.data.statistics.store_admins || 0,
            total: res.data.statistics.total_admins || 0
          }
        }
      } catch (error) {
        console.error('获取管理员列表失败:', error)
        this.$message.error(error.message || '获取管理员列表失败')
      } finally {
        this.loading = false
      }
    },

    async loadStatistics() {
      // 统计数据现在从 loadAdminList 中获取，此方法保留用于单独刷新统计
      // 直接调用 loadAdminList 即可
      await this.loadAdminList()
    },
    
    handleSearch() {
      this.pagination.currentPage = 1
      this.loadAdminList()
    },
    
    resetSearch() {
      this.searchForm = {
        keyword: '',
        roleType: null,
        isActive: null
      }
      this.pagination.currentPage = 1
      this.loadAdminList()
    },

    refreshData() {
      this.$message.info('正在刷新数据...')
      this.loadAdminList()
    },
    
    handleSizeChange(val) {
      this.pagination.pageSize = val
      this.pagination.currentPage = 1
      this.loadAdminList()
    },
    
    handleCurrentChange(val) {
      this.pagination.currentPage = val
      this.loadAdminList()
    },
    
    handleCreate() {
      this.isEdit = false
      this.dialogVisible = true
    },
    
    handleEdit(row) {
      this.isEdit = true
      this.adminForm = {
        id: row.id,
        username: row.username,
        real_name: row.realName,
        phone: row.phone,
        email: row.email,
        role_type: row.roleType,
        store_id: row.store_info ? row.store_info.id : null
      }
      this.dialogVisible = true
    },
    
    async handleSubmit() {
      try {
        await this.$refs.adminForm.validate()
        
        // 构建请求数据，确保字段名称与API文档一致
        const requestData = {
          username: this.adminForm.username,
          real_name: this.adminForm.real_name,
          phone: this.adminForm.phone,
          email: this.adminForm.email,
          role_type: this.adminForm.role_type
        }
        
        // 如果是新增，添加密码
        if (!this.isEdit) {
          requestData.password = this.adminForm.password
        }
        
        // 如果是门店管理员，添加 store_id
        if (this.adminForm.role_type === 3 && this.adminForm.store_id) {
          requestData.store_id = parseInt(this.adminForm.store_id)
        }
        
        // 移除空值字段
        Object.keys(requestData).forEach(key => {
          if (requestData[key] === null || requestData[key] === '' || requestData[key] === undefined) {
            delete requestData[key]
          }
        })
        
        if (this.isEdit) {
          await updateAdmin(this.adminForm.id, requestData)
          this.$message.success('管理员信息更新成功')
        } else {
          await createAdmin(requestData)
          this.$message.success('管理员创建成功')
        }
        
        this.dialogVisible = false
        this.loadAdminList()
      } catch (error) {
        console.error('操作失败:', error)
        this.$message.error(error.message || (this.isEdit ? '更新失败' : '创建失败'))
      }
    },
    
    async handleToggleStatus(row) {
      try {
        const action = row.isActive ? '禁用' : '启用'
        await this.$confirm(`确定要${action}该管理员吗？`, '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        })
        
        await updateAdminStatus(row.id, {
          isActive: !row.isActive,
          reason: `管理员${action}操作`
        })
        
        this.$message.success(`${action}成功`)
        this.loadAdminList()
      } catch (error) {
        if (error !== 'cancel') {
          console.error('状态更新失败:', error)
          this.$message.error(error.message || '操作失败')
        }
      }
    },
    
    async handleResetPassword(row) {
      try {
        const { value } = await this.$prompt('请输入新密码', '重置密码', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          inputType: 'password',
          inputValidator: (value) => {
            if (!value || value.length < 6) {
              return '密码长度不能少于6位'
            }
            return true
          }
        })
        
        await resetAdminPassword(row.id, { new_password: value })
        this.$message.success('密码重置成功')
      } catch (error) {
        if (error !== 'cancel') {
          console.error('密码重置失败:', error)
          this.$message.error(error.message || '密码重置失败')
        }
      }
    },
    
    async handleDelete(row) {
      try {
        await this.$confirm('删除后无法恢复，确定要删除该管理员吗？', '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        })
        
        await deleteAdmin(row.id)
        this.$message.success('删除成功')
        this.loadAdminList()
      } catch (error) {
        if (error !== 'cancel') {
          console.error('删除失败:', error)
          this.$message.error(error.message || '删除失败')
        }
      }
    },
    
    resetForm() {
      this.adminForm = {
        username: '',
        password: '',
        real_name: '',
        phone: '',
        email: '',
        role_type: null,
        store_id: null
      }
      if (this.$refs.adminForm) {
        this.$refs.adminForm.resetFields()
      }
    },
    
    getRoleClass(roleType) {
      const classMap = {
        1: 'role-super',
        2: 'role-system', 
        3: 'role-store'
      }
      return classMap[roleType] || 'role-default'
    },

    getRoleDescClass(roleType) {
      const classMap = {
        1: 'desc-super',
        2: 'desc-system',
        3: 'desc-store'
      }
      return classMap[roleType] || 'desc-default'
    },

    getRoleIcon(roleType) {
      const iconMap = {
        1: 'el-icon-s-custom',
        2: 'el-icon-setting',
        3: 'el-icon-office-building'
      }
      return iconMap[roleType] || 'el-icon-user'
    },

    getRoleText(roleType) {
      const textMap = {
        1: '超级管理员',
        2: '系统管理员',
        3: '门店管理员'
      }
      return textMap[roleType] || '未知角色'
    },

    getRoleDescription(roleType) {
      const descMap = {
        1: '拥有系统的最高权限，可以管理所有功能和用户',
        2: '拥有系统管理权限，可以管理大部分系统功能',
        3: '仅拥有门店管理权限，只能管理关联的门店信息'
      }
      return descMap[roleType] || '权限说明'
    },
    
    formatDate(dateString) {
      if (!dateString) return '-'
      const date = new Date(dateString)
      return date.toLocaleString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      })
    }
  }
}
</script>

<style scoped>
.admin-list {
  min-height: 100vh;
  background: #f5f7fa;
  animation: fadeInUp 0.6s ease-out;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* 页面头部 */
.page-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 25px 40px;
  margin-bottom: 30px;
  box-shadow: 0 2px 10px rgba(102, 126, 234, 0.3);
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  max-width: 1400px;
  margin: 0 auto;
}

.header-left {
  display: flex;
  align-items: center;
  flex: 1;
}

.header-info {
  flex: 1;
}

.page-title {
  font-size: 28px;
  font-weight: 600;
  margin: 0 0 8px 0;
  display: flex;
  align-items: center;
  gap: 12px;
}

.page-title i {
  font-size: 32px;
}

.page-subtitle {
  font-size: 16px;
  opacity: 0.9;
  margin: 0;
  line-height: 1.5;
}

.header-actions {
  display: flex;
  gap: 12px;
}

.header-actions .el-button {
  background: rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: white;
  font-weight: 500;
}

.header-actions .el-button--primary {
  background: rgba(255, 255, 255, 0.95);
  color: #667eea;
  border-color: transparent;
}

.header-actions .el-button:hover {
  background: rgba(255, 255, 255, 0.3);
  border-color: rgba(255, 255, 255, 0.5);
}

.header-actions .el-button--primary:hover {
  background: white;
  color: #667eea;
}

/* 主内容区域 */
.main-content {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 30px;
}

/* 统计卡片 */
.stats-section {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;
  margin-bottom: 30px;
}

.stat-card {
  background: white;
  border-radius: 20px;
  padding: 24px 20px;
  display: flex;
  align-items: center;
  gap: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border: 1px solid #f1f3f4;
  position: relative;
  overflow: hidden;
}

.stat-card:hover {
  transform: translateY(-6px);
  box-shadow: 0 16px 40px rgba(0, 0, 0, 0.12);
}

.stat-icon {
  width: 60px;
  height: 60px;
  border-radius: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 26px;
  background: white;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
  position: relative;
}

.stat-icon::before {
  content: '';
  position: absolute;
  inset: -2px;
  border-radius: 22px;
  padding: 2px;
  mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
  mask-composite: exclude;
  -webkit-mask-composite: xor;
}

.stat-icon.super::before {
  background: linear-gradient(135deg, #f093fb, #f5576c);
}

.stat-icon.super i {
  color: #f093fb;
}

.stat-icon.system::before {
  background: linear-gradient(135deg, #f39c12, #e67e22);
}

.stat-icon.system i {
  color: #f39c12;
}

.stat-icon.store::before {
  background: linear-gradient(135deg, #43e97b, #38f9d7);
}

.stat-icon.store i {
  color: #43e97b;
}

.stat-icon.total::before {
  background: linear-gradient(135deg, #4facfe, #00f2fe);
}

.stat-icon.total i {
  color: #4facfe;
}

.stat-icon i {
  z-index: 1;
}

.stat-info {
  flex: 1;
}

.stat-value {
  font-size: 32px;
  font-weight: 700;
  color: #303133;
  line-height: 1;
  margin-bottom: 4px;
}

.stat-label {
  font-size: 14px;
  color: #666;
  font-weight: 500;
}

/* 筛选区域 */
.filter-section {
  margin-bottom: 30px;
}

.filter-card {
  background: white;
  border-radius: 16px;
  padding: 25px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid #f1f3f4;
}

.filter-form {
  margin: 0;
}

.form-row {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
  margin-bottom: 20px;
}

.form-item {
  display: flex;
  flex-direction: column;
}

.form-label {
  font-size: 14px;
  font-weight: 600;
  color: #606266;
  margin-bottom: 8px;
}

.form-actions {
  text-align: center;
}

.form-actions .el-button {
  min-width: 120px;
  margin: 0 10px;
}

/* 表格区域 */
.table-section {
  margin-bottom: 30px;
}

.table-card {
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid #f1f3f4;
  overflow: hidden;
}

.table-header {
  padding: 25px 30px 15px;
  border-bottom: 1px solid #f0f2f5;
  background: linear-gradient(135deg, #fafbfc, #f8f9fa);
}

.table-header h3 {
  font-size: 18px;
  font-weight: 600;
  color: #303133;
  margin: 0 0 5px 0;
  display: flex;
  align-items: center;
  gap: 10px;
}

.table-header h3 i {
  color: #667eea;
  font-size: 20px;
}

.table-header p {
  font-size: 14px;
  color: #666;
  margin: 0;
}

/* 表格样式 */
.admin-table :deep(.el-table) {
  border: none;
}

.admin-table :deep(.el-table th) {
  background: #fafbfc;
  border: none;
  color: #606266;
  font-weight: 600;
}

.admin-table :deep(.el-table td) {
  border: none;
  padding: 15px 10px;
}

.admin-table :deep(.el-table--border::after) {
  display: none;
}

.admin-table :deep(.el-table::before) {
  display: none;
}

.admin-id {
  font-weight: 600;
  color: #667eea;
  font-family: monospace;
}

.user-info {
  display: flex;
  align-items: center;
  gap: 12px;
}

.user-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(135deg, #667eea, #764ba2);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 18px;
  flex-shrink: 0;
}

.user-details {
  flex: 1;
}

.username {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
}

.real-name {
  font-size: 13px;
  color: #909399;
}

.contact-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.contact-item {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #606266;
}

.contact-item i {
  color: #909399;
}

.role-tag {
  border-radius: 20px;
  padding: 6px 12px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 5px;
}

.role-tag.role-super {
  background: #fef0f0;
  color: #f56c6c;
  border-color: #fbc4c4;
}

.role-tag.role-system {
  background: #fdf6ec;
  color: #e6a23c;
  border-color: #f5dab1;
}

.role-tag.role-store {
  background: #f0f9ff;
  color: #409eff;
  border-color: #b3d8ff;
}

.store-info {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #606266;
}

.store-info i {
  color: #909399;
}

.no-store {
  color: #c0c4cc;
  font-style: italic;
}

.status-wrapper {
  display: flex;
  justify-content: center;
}

.status-tag {
  border-radius: 20px;
  padding: 6px 12px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 5px;
}

.status-tag.status-active {
  background: #f0f9ff;
  color: #67c23a;
  border-color: #c2e7b0;
}

.status-tag.status-inactive {
  background: #fef0f0;
  color: #f56c6c;
  border-color: #fbc4c4;
}

.login-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.login-item {
  font-size: 12px;
  color: #909399;
  display: flex;
  align-items: center;
  gap: 4px;
}

.login-value {
  font-size: 13px;
  color: #606266;
  font-family: monospace;
}

.action-buttons {
  display: flex;
  gap: 8px;
  justify-content: center;
}

.action-btn {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
}

.edit-btn:hover {
  background: #667eea !important;
  border-color: #667eea !important;
  transform: scale(1.1);
}

.reset-btn:hover {
  background: #e6a23c !important;
  border-color: #e6a23c !important;
  transform: scale(1.1);
}

.toggle-btn:hover {
  transform: scale(1.1);
}

.delete-btn:hover {
  background: #f56c6c !important;
  border-color: #f56c6c !important;
  transform: scale(1.1);
}

/* 分页 */
.pagination-wrapper {
  padding: 20px 30px;
  text-align: right;
  background: #fafbfc;
  border-top: 1px solid #f0f2f5;
}

/* 对话框样式 */
.admin-dialog :deep(.el-dialog) {
  border-radius: 16px;
  overflow: hidden;
}

.admin-dialog :deep(.el-dialog__header) {
  background: linear-gradient(135deg, #667eea, #764ba2);
  color: white;
  padding: 20px 25px;
}

.admin-dialog :deep(.el-dialog__title) {
  color: white;
  font-weight: 600;
  font-size: 18px;
}

.admin-dialog :deep(.el-dialog__headerbtn .el-dialog__close) {
  color: rgba(255, 255, 255, 0.8);
  font-size: 20px;
}

.admin-dialog :deep(.el-dialog__body) {
  padding: 0;
}

.dialog-content {
  padding: 25px;
  max-height: 70vh;
  overflow-y: auto;
}

.admin-form :deep(.el-form-item) {
  margin-bottom: 20px;
}

.form-section {
  margin-bottom: 30px;
}

.form-section:last-child {
  margin-bottom: 0;
}

.form-section h4 {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
  margin: 0 0 20px 0;
  display: flex;
  align-items: center;
  gap: 8px;
  padding-bottom: 10px;
  border-bottom: 2px solid #f0f2f5;
}

.form-section h4 i {
  color: #667eea;
  font-size: 18px;
}

.form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
}

.form-item.full-width {
  grid-column: 1 / -1;
}

.role-description {
  margin-top: 15px;
}

.desc-item {
  padding: 15px;
  border-radius: 10px;
  border: 1px solid #f0f2f5;
  display: flex;
  align-items: flex-start;
  gap: 12px;
  transition: all 0.3s ease;
}

.desc-item.desc-super {
  background: #fef0f0;
  border-color: #fbc4c4;
}

.desc-item.desc-system {
  background: #fdf6ec;
  border-color: #f5dab1;
}

.desc-item.desc-store {
  background: #f0f9ff;
  border-color: #b3d8ff;
}

.desc-item i {
  font-size: 20px;
  margin-top: 2px;
}

.desc-item.desc-super i {
  color: #f56c6c;
}

.desc-item.desc-system i {
  color: #e6a23c;
}

.desc-item.desc-store i {
  color: #409eff;
}

.desc-content {
  flex: 1;
}

.desc-content h5 {
  font-size: 14px;
  font-weight: 600;
  color: #303133;
  margin: 0 0 5px 0;
}

.desc-content p {
  font-size: 13px;
  color: #666;
  margin: 0;
  line-height: 1.4;
}

/* 对话框底部 */
.dialog-footer {
  text-align: right;
  padding: 20px 25px;
  background: #fafbfc;
  border-top: 1px solid #f0f2f5;
}

/* 响应式设计 */
@media (max-width: 1200px) {
  .stats-section {
    grid-template-columns: repeat(2, 1fr);
  }
  
  .form-row {
    grid-template-columns: repeat(2, 1fr);
  }
  
  .form-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 768px) {
  .page-header {
    padding: 20px;
  }
  
  .page-title {
    font-size: 24px;
  }
  
  .main-content {
    padding: 0 15px;
  }
  
  .header-content {
    flex-direction: column;
    gap: 15px;
    text-align: center;
  }
  
  .stats-section {
    grid-template-columns: 1fr;
  }
  
  .form-row {
    grid-template-columns: 1fr;
  }
  
  .action-buttons {
    flex-direction: column;
    gap: 5px;
  }
}

/* Element UI 样式覆盖 */
:deep(.el-form-item) {
  margin-bottom: 0;
}

:deep(.el-input__inner) {
  border-radius: 8px;
  border: 1px solid #e4e7ed;
  transition: all 0.3s ease;
}

:deep(.el-input__inner:focus) {
  border-color: #667eea;
  box-shadow: 0 0 0 2px rgba(102, 126, 234, 0.2);
}

:deep(.el-select) {
  width: 100%;
}

:deep(.el-pagination) {
  text-align: right;
}
</style> 